import type { FC } from 'react'
import { useEffect, useState } from 'react'

import { Input, Select, Checkbox } from '@skytech/ui'
import { Container } from '@skytech/ui/Container'
import { getQueryFilter } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useSearchParamsByKey } from '@/hooks/searchParams'
import { useSearch } from '@/hooks/searchParams'
import { useLocalDate } from '@/hooks/use-local-date'
import useUser from '@/hooks/user'
import adminResources from '@/modules/admin/resources'
import type { Invoice } from '@/modules/admin/resources'

import queries from '../queries'

import { InvoicesList } from './InvoicesList'

export const InvoicesListPage: FC = () => {
  const user = useUser()

  const navigate = useNavigate()
  const translate = useTranslation()
  const { formatDate } = useLocalDate()

  const stringMatcher = (item: Invoice) => [
    item?.vendorName,
    item.invoiceNo,
    formatDate({ date: item.date }),
    formatDate({ date: item?.dueDate }),
    item?.invoiceAmount,
  ]

  const [queryInvoiceType, setQueryInvoiceType] = useSearchParamsByKey('Invoice', 'customer')

  const [queryShowAllCompanies, setQueryShowAllCompanies] = useSearchParamsByKey('all-companies')
  const [showAllCompaniesFilter, setAllCompaniesFilter] = useState(Boolean(queryShowAllCompanies))

  const { searchFilter, setSearchFilter } = useSearch()

  useEffect(() => {
    setAllCompaniesFilter(queryShowAllCompanies ? JSON.parse(queryShowAllCompanies) : false)
  }, [queryShowAllCompanies])

  const { data: invoicesList } = useQuery(
    queries.adminInvoicesList(),
    adminResources.getInvoicesList
  )

  const invoicesFiltered =
    invoicesList?.filter(invoiceObject => {
      let correctType = false
      if (queryInvoiceType === 'customer') correctType = invoiceObject.isInternalVendor
      if (queryInvoiceType === 'vendor') correctType = !invoiceObject.isInternalVendor

      const allCompanies = showAllCompaniesFilter
        ? true
        : invoiceObject.customerId === user?.customerId

      const isQueryFilterMet = getQueryFilter(searchFilter, stringMatcher)(invoiceObject)
      return isQueryFilterMet && correctType && allCompanies
    }) || []

  if (!invoicesList) return null

  return (
    <Container onSwipedLeft={() => navigate('/')}>
      <Container.Header>
        <Translation id="Invoices" />
      </Container.Header>

      <div className="flex w-full flex-wrap items-center justify-start gap-2">
        <div>
          <Input
            id="department-search"
            value={searchFilter}
            placeholder={translate('Search')}
            onChange={e => setSearchFilter(e.target.value)}
          />
        </div>

        <Select
          id="invoice-type"
          value={queryInvoiceType || 'customer'}
          options={[
            { value: 'customer', label: translate('Skytech Invoices') },
            { value: 'vendor', label: translate('Vendor Invoices') },
          ]}
          onChange={option => setQueryInvoiceType(option?.value || '')}
        />

        <Checkbox
          id="show-all-companies-input"
          marginTop={0}
          marginLeft={0}
          checked={queryShowAllCompanies ? JSON.parse(queryShowAllCompanies) : false}
          onChange={() => {
            const updatedShowHidden = queryShowAllCompanies
              ? JSON.parse(queryShowAllCompanies)
                ? 'false'
                : 'true'
              : 'true'
            setQueryShowAllCompanies(updatedShowHidden)
          }}
          label={<Translation id="All companies" />}
        />
      </div>

      <InvoicesList invoices={invoicesFiltered} searchFilter={searchFilter} />
    </Container>
  )
}
